import React, { useState } from "react";
import { PulseLoader } from "react-spinners";
import { useMutation } from "@tanstack/react-query";

import FormHeader from "../components/form-header.jsx";
import Button from "../components/button.jsx";
import Page from "./page.jsx";
import Alert from "../components/alert.jsx";
import SubmissionContext from "../utilities/submission-context";
import shouldAllowSubmit from "../utilities/should-allow-submit";
import shouldAllowProgress from "../utilities/should-allow-progress";
import passBackSubmissionState from "../utilities/pass-back-submission-state";
/**
 * @typedef {import('../../types').PageEntity} PageEntity
 * @typedef {import('../../types').SubmissionData} SubmissionData
 * @typedef {import('../../types').Component} Component
 * @typedef {import('../../types').FeedbackEntity} FeedbackEntity
 * @typedef {import('../../types').Form1} Form
 */

/**
 * Renders the honeypot input element
 * @type {React.FC}
 * @returns {React.ReactElement} - honeypot element
 */
function HoneyPot() {
  return <input id="honeypot-field" type="text" className="fr-hidden" />;
}

/**
 * @typedef {Form} FormProps
 */

/**
 * @typedef {object} FormState - The state of the form
 * @property {Page} currentPage - The current page
 */

/**
 * @param {FormProps} props - props
 * @returns {React.JSX.Element} - The form
 */
function Form({
  formId,
  pages,
  respondentId,
  passThroughData,
  submissionCallbackUrl,
  quiz: isQuiz, // because the api is irresponsible
  showTitle,
  feedback,
  title,
  flattenPages,
  pageNavigationNext,
}) {
  // set initial values we need using state here so we can augment them later
  // @see https://stackoverflow.com/questions/41030361/how-to-update-react-context-from-inside-a-child-component
  const [submissionData, setSubmissionData] = useState({
    formId,
    passThroughData,
    respondentId,
    submissionCallbackUrl,
    feedback,
    components: [],
  });
  /** @type {object} */
  const [currentPage, setCurrentPage] = useState(pages[0]);
  /** @type {object} quizResponse */
  const [quizResponse, setQuizReponse] = useState(null);
  const [showIncompleteComponents, setShowIncompleteComponents] =
    useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const allComponents = pages?.map((page) => page.components).flat();
  const allInputComponents = allComponents?.filter(
    (component) => component?.type !== "text",
  );
  const questionCount = allInputComponents?.length ?? 0;
  const pageCount = flattenPages ? 0 : pages?.length ?? 0;
  const currentPageNumber = Number(currentPage.pageId);
  const currentPageIndex =
    pages?.map((page) => Number(page.pageId)).indexOf(currentPageNumber) ?? 0;
  const isLastPage = pageCount === currentPageNumber;
  const isSamePageTitle = title === currentPage.title;

  // To be able to disable the submit button until
  // all required fields are filled out, we need to compare
  // the submitted data with the required components.
  /**
   * @type {Component[] | null | []}
   */
  const allRequiredComponents = allInputComponents?.filter(
    (component) => component?.validation?.required,
  );

  /**
   * @type {Component[] | null | []}
   */
  const pageRequiredComponents = currentPage.components.filter(
    (component) => component.validation.required,
  );

  const shouldSubmit = shouldAllowSubmit(
    allRequiredComponents,
    submissionData.components,
  );
  const shouldProgress = shouldAllowProgress(
    pageRequiredComponents,
    submissionData.components,
  );

  const apiUrl =
    window.formRendererData?.formCallbackUrl ?? import.meta.env.VITE_API_URL;
  const apiKey =
    window.formRendererData?.apiKey ?? import.meta.env.VITE_API_KEY;

  const submissionUrl =
    submissionCallbackUrl ?? isQuiz
      ? `${apiUrl}submission/result`
      : `${apiUrl}submission`;

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      try {
        const response = await fetch(submissionUrl, {
          method: "POST",
          headers: {
            "x-api-key": apiKey,
          },
          body: JSON.stringify(submissionData),
        });
        const data = await response.json();
        setQuizReponse(data);
      } catch (error) {
        passBackSubmissionState(isQuiz, null, error);
        setQuizReponse(error);
      }
    },
  });

  /**
   * run the useQuery hook with data sent up using context
   * @param {React.SyntheticEvent<HTMLFormElement> | MouseEvent} event - event
   * @returns {Promise<void>} - it doesn't
   */
  async function onSubmit(event) {
    setHasSubmitted(true);
    event.preventDefault();
    // @ts-ignore not void
    mutate(submissionData);
  }

  /**
   *
   * @returns {void}
   */
  function setNextPage() {
    if (!shouldProgress) {
      setShowIncompleteComponents(true);
      return;
    }
    if (pages) {
      setCurrentPage(pages[currentPageIndex + 1]);
    }
  }
  /**
   *
   * @returns {void}
   */
  function setPreviousPage() {
    if (pages) {
      setCurrentPage(pages[currentPageIndex - 1]);
    }
  }

  if (isPending) {
    return (
      <div className="fr-min-h-20 fr-flex fr-flex-col fr-min-w-full fr-h-screen fr-items-center fr-justify-start">
        <PulseLoader size={30} color="#36d7b7" />
        <p>Loading results ...</p>
      </div>
    );
  }

  if (quizResponse?.message) {
    return (
      <div className="fr-min-h-20 fr-flex fr-flex-col fr-min-w-full fr-h-screen fr-items-center fr-justify-start">
        <Alert
          title="Fatal error response:"
          message={quizResponse?.message}
          type="error"
        />
      </div>
    );
  }

  if (quizResponse?.passed === false) {
    passBackSubmissionState(isQuiz, quizResponse);
    return (
      <div className="fr-min-h-20 fr-flex fr-flex-col fr-min-w-full fr-h-screen fr-items-center fr-justify-start">
        <Alert
          title={feedback.failure}
          message={`Score: ${quizResponse.score}`}
          type="error"
        />
      </div>
    );
  }
  if (quizResponse?.passed === true) {
    passBackSubmissionState(isQuiz, quizResponse);
    return (
      <div className="fr-min-h-20 fr-flex fr-flex-col fr-min-w-full fr-h-screen fr-items-center fr-justify-start">
        <Alert
          title={feedback.success}
          message={`Score: ${quizResponse.score}`}
          type="success"
        />
      </div>
    );
  }
  if (allComponents?.length === 0) {
    return (
      <div className="fr-min-h-20 fr-flex fr-flex-col fr-min-w-full fr-h-screen fr-items-center fr-justify-start">
        <Alert
          title="No components to render"
          message="this quiz doesn't seem to contain any questions"
          type="error"
        />
      </div>
    );
  }

  return (
    <SubmissionContext.Provider value={{ submissionData, setSubmissionData }}>
      <form onSubmit={(event) => onSubmit(event)}>
        <FormHeader
          showTitle={showTitle}
          title={title}
          isQuiz={isQuiz}
          questionCount={questionCount}
          pageCount={pageCount}
          currentPageNumber={currentPageNumber}
        />
        <Page
          showTitle={currentPage.showTitle && !isSamePageTitle}
          title={currentPage.title}
          components={currentPage.components}
          key={currentPage.pageId}
          showIncompleteFeedback={showIncompleteComponents}
        />
        <HoneyPot />
        <div className="fr-flex fr-gap-4">
          {!flattenPages && pageCount > 0 && (
            <>
              <Button
                type="button"
                value="← Previous"
                disabled={currentPageNumber === 1}
                onClick={setPreviousPage}
              />
              <Button
                type="button"
                value={`${pageNavigationNext ?? "Next"} →`}
                onClick={setNextPage}
                disabled={!shouldProgress || isLastPage}
              />
            </>
          )}
          {isLastPage && (
            <Button
              type="submit"
              value="submit"
              disabled={!shouldSubmit && !hasSubmitted}
            />
          )}
        </div>
      </form>
    </SubmissionContext.Provider>
  );
}

export default Form;
